import { useEffect, useState, useCallback, useRef } from "react";
import api from "./api/client";
import * as debug from "./utils/debug";
import "./App.css";

function App() {
  
  // ==================================================
  // App config
  // ==================================================
  //debug.enable()

  // ==================================================
  // UI state
  // ==================================================
  const UI_STATE = {
    NULL: "null",
    LOADING: "loading",
    SELECT_QUIZ: "select_quiz",
    QUESTION: "question",
    ANSWERED_CORRECT: "answered_correct",
    ANSWERED_FALSE: "answered_false",
    FINISHED: "finished",
    ERROR: "error",
    DISABLED: "disabled"
  };

  // ==================================================
  // REACT states
  //
  // apiAlive: indicates that api health check succeeded
  // quizzes: the quizzes list provided by backend
  // quiz: the selected quiz object provided by backend
  // question: the current loaded question object provided by backend
  // 
  // Notes:
  // * apiAlive must initially be true to see the 'Loading...' page first.
  // * question contains general information about its parent quiz, e.g. progress state
  // 
  // ==================================================
  const [apiAlive, setApiAlive] = useState(true);
  const [quizzes, setQuizzes] = useState([]);
  const [quiz, setQuiz] = useState(null);
  const [question, setQuestion] = useState(null);
  const [selectedOption, setSelectedOption] = useState([]);
  const [answerResult, setAnswerResult] = useState(null);
  const [layout, setLayout] = useState("center-center");
  const [darkMode, setDarkMode] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [shuffleq, setShuffleq] = useState(false);
  const [isAnswering, setIsAnswering] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [uiState, setUiState] = useState(UI_STATE.NULL);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [quizPassword, setQuizPassword] = useState("");
  const [pendingQuiz, setPendingQuiz] = useState(null);
  const [authError, setAuthError] = useState("");
  const [showPassword, setShowPassword] = useState(false)

  // ==================================================
  // REACT refs
  // ==================================================
  const timeoutRef = useRef(null);

  // ==================================================
  // REACT derived values
  // ==================================================
  //

  // ==================================================
  // HANDLER functions
  // ==================================================
  const checkApi = useCallback(async () => {
    try {
      await api.health();
      setApiAlive(true);
    } catch {
      setApiAlive(false);
    }
  }, []);

  window.addEventListener("error", () => {
    setApiAlive(false);
  });

  const computeUIState = () => {
    // debug.log("quizzes.length: ", quizzes.length)

    let state = UI_STATE.NULL
    if (!apiAlive)
      state = UI_STATE.ERROR;
    else if (quizzes.length === 0)
      state = UI_STATE.LOADING;
    else if (!quiz) 
      state = UI_STATE.SELECT_QUIZ;
    else if (question) {
      if (!answerResult && !question.quiz_finished)
        state = UI_STATE.QUESTION;
      else if (answerResult && answerResult.correct)
        state = UI_STATE.ANSWERED_CORRECT
      else if (answerResult)
        state = UI_STATE.ANSWERED_FALSE;
      else
        state = UI_STATE.FINISHED;
    }

    debug.log("UI_STATE: ", state);

    return state
  };

  const resetState = () => {
    setSelectedOption([]);
    setAnswerResult(null);

    setShowTooltip(false);
    clearTimeout(timeoutRef.quiz_current);

    setIsAnswering(false);
  };

  const toggleAnswer = useCallback((key) => {
    if (answerResult || isAnswering) return;

    setSelectedOption(prev => {

      // ------------------------------------
      // MULTIPLE CHOICE MODE
      // ------------------------------------
      if (question.question_type === "multiple-choice") {
        return prev.includes(key)
          ? prev.filter(x => x !== key)
          : [...prev, key];
      }

      // ------------------------------------
      // SINGLE CHOICE MODE (Radio Button)
      // ------------------------------------
      return [key];
    });

  }, [question, answerResult, isAnswering]);

  // -----------------------------
  // LOAD QUIZ (SESSION-BASED)
  // -----------------------------
  const loadQuestion = async (q = null, check_protected = true) => {
    debug.log(`app-action: load-question ${JSON.stringify(q?.name ?? '')}`)

    let quiz_name = q ? q.name : ""
    let quiz_protected = q ? q.protected : false

    // debug.log("name =", quiz_name);
    // debug.log("shuffle =", shuffle);
    // debug.log("shuffleq =", shuffleq);
    // debug.log(JSON.stringify({
    //   quiz_name: quiz_name,
    //   shuffle,
    //   shuffleq
    // }));

    // Passwort erforderlich: Passwortdialog öffnen
    if (check_protected && quiz_protected) {
      setPendingQuiz(q);
      setShowPasswordModal(true);
      return;
    }

    // Kein Passwort nötig: direkt laden
    const data = await api.loadQuestion({
      quiz_name: quiz_name,
      shuffle: shuffle,
      shuffleq: shuffleq,
    });

    setQuestion(data);

    setShuffle(data["shuffle"])
    setShuffleq(data["shuffleq"])

    resetState()
    setQuiz(q);
  };

  // -----------------------------
  // UNLOCK PROTECTED
  // -----------------------------
  const unlockQuiz = async () => {
    let q = quiz
    debug.log(`app-action: unlock-quiz ${JSON.stringify(q?.name ?? '')}`)

    try {
      const data = await api.unlockQuiz({
        quiz_name: pendingQuiz.name,
        password: quizPassword
      });

      setShowPasswordModal(false);
      setQuiz(pendingQuiz);

      await loadQuestion(pendingQuiz, false);
      
    } catch {
      setAuthError("Falsches Passwort");
    }
  };

  // -----------------------------
  // ANSWER QUESTION
  // -----------------------------
  const answer = useCallback(async () => {
    let q = quiz
    debug.log(`app-action: answer ${JSON.stringify(q?.name ?? '')}`)

    if (selectedOption.length === 0 || isAnswering || !question || question.quiz_finished)
      return;

    setIsAnswering(true);

    const data = await api.answer({
      quiz_name: question.quiz_name,
      question_id: question.question_id,
      answers: selectedOption
    });

    setAnswerResult(data)
    
    setIsAnswering(false);
  }, [selectedOption, isAnswering, question]);

  // -----------------------------
  // NEXT QUESTION
  // -----------------------------
  const next = useCallback(() => {
    let q = quiz
    debug.log(`app-action: next ${JSON.stringify(q?.name ?? '')}`)

    if (isAnswering)
      return;

    resetState()

    loadQuestion(quiz, false);
  }, [isAnswering, question]);

  // -----------------------------
  // STOP QUIZ
  // -----------------------------
  const stop = async () => {
    let q = quiz
    debug.log(`app-action: stop ${JSON.stringify(q?.name ?? '')}`)

    if (q) {
      /*const data =*/ await api.stop({
        quiz_name: q?.name
      });
    }

    setQuiz(null)

    resetState()
    loadQuizzes();
  };

  // -----------------------------
  // RESTART QUIZ
  // -----------------------------
  const restart = async () => {
    let q = quiz
    debug.log(`app-action: restart ${JSON.stringify(q?.name ?? '')}`)

    if (!quiz)
      return

    // debug.log(`Restart ${question.quiz_name}`)
    // debug.log("shuffle =", shuffle);
    // debug.log("shuffleq =", shuffleq);
    // debug.log(JSON.stringify({
    //   quiz_name: question.quiz_name,
    //   shuffle,
    //   shuffleq
    // }));

    /*const data =*/ await api.restart({
      quiz_name: q?.name,
      shuffle: shuffle,
      shuffleq: shuffleq
    });

    resetState();
    loadQuestion(quiz, false);
  };

  // -----------------------------
  // Download Result
  // -----------------------------
  const downloadResult = async () => {
    let q = quiz
    debug.log(`app-action: download-result ${JSON.stringify(q?.name ?? '')}`)

    try {
      const data = await api.exportResult();

      const blob = new Blob(
        [data.result],
        { type: "text/plain" }
      );

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "quiz_result.txt";

      document.body.appendChild(a);

      a.click();

      a.remove();

      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error("Export failed:", err);
      setApiAlive(false);
    }
  };

  // ==================================================
  // REACT effects
  //
  // Absicherung aller useEffects, ohne Failed to fetch Runtime-Crashs
  // * kein „Uncaught runtime error“
  // * kein React overlay crash
  // * API down → UI bleibt stabil
  // * mit konsistenter Error-Handling-Strategie
  // ==================================================
  // -----------------------------
  // INITIAL API CHECK
  // -----------------------------
  useEffect(() => {
    checkApi();
  }, []);

  // --------------------------------------------------
  // UI STATE
  // Dieser useEffect synchronisiert uiState jedes Mal neu, wenn sich Quizdaten, Ergebnis oder
  // API-Status ändern, sodass die UI immer konsistent zum aktuellen App-Zustand bleibt.
  // --------------------------------------------------
  useEffect(() => {
    setUiState(computeUIState());
  }, [apiAlive, quizzes, quiz, question, answerResult]);

  // --------------------------------------------------
  // LOAD QUIZ LIST
  // Dieser useEffect wird genau einmal nach dem initialen Render der Komponente von React
  // selbst ausgeführt, um beim Laden der Seite die Quizliste asynchron vom Backend zu laden.
  // --------------------------------------------------
  const loadQuizzes = async () => {
    try {
      const data = await api.getQuizzes();
      setQuizzes(data);
    } catch (err) {
      console.error("Quiz list load failed:", err);
      setApiAlive(false);
      setQuizzes([]); // fallback statt crash
    }
  };

  useEffect(() => {
    loadQuizzes();
  }, []);

  // -----------------------------
  // Keyboard Handling
  // -----------------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toUpperCase();

      if (!question) return;

      if (e.code === "Space") e.preventDefault();

      if (isAnswering) return;

      if (["A", "B", "C", "D"].includes(key)) {
        toggleAnswer(key);
        return;
      }

      const isContinueKey = e.code === "Enter" || e.code === "Space";

      // submit answer
      if (isContinueKey && selectedOption.length > 0 && !answerResult) {
        answer();
        return;
      }

      // next question
      if (isContinueKey && answerResult) {
        next();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [question, selectedOption, answerResult, isAnswering, toggleAnswer, answer, next]);

  // ==================================================
  // JSX
  // ==================================================
  // --------------------------------------------------
  // API ERROR PAGE
  // --------------------------------------------------
  function ApiErrorPage({ onRetry }) {
    return (
      // <div className="error-page">
      <div className={`${layoutClass} ${themeClass}`}>
        {/* <div className="error-card"> */}
        <div className={`card error`}>

          <div className="error-icon">⚠️</div>

          <h1>Service Unavailable</h1>

          <p>
            The quiz service is currently not reachable.
            Please check your connection or try again later.
          </p>

          <div className="error-actions">
            <button className="retry-button" onClick={onRetry}>
              Retry
            </button>

            <button
              className="secondary-button"
              onClick={() => window.location.reload()}
            >
              Reload App
            </button>
          </div>

          <div className="error-hint">
            If the problem persists, the backend might be down.
          </div>

        </div>
      </div>
    );
  }

  function ShowApiErrorPage() {
      return <ApiErrorPage onRetry={checkApi} />;
  }

  // --------------------------------------------------
  // Controller ERROR PAGE
  // --------------------------------------------------
  function ShowControllerErrorPage(err_message) {
    return (
      <div className={`${layoutClass} ${themeClass}`}>
        {/* <div className="error-card"> */}
        <div className={`card error`}>

          <div className="error-icon">⚠️</div>

          <h1>Internal Controller Error</h1>

          <div className="error-text">
            <div>The quiz controller is in error state:</div>
            <div><b>{ err_message }</b></div>
            <div>Please inform admin.</div>
          </div>

          <div className="error-actions">
            <button
              className="secondary-button"
              onClick={() => window.location.reload()}
            >
              Reload App
            </button>
          </div>

          <div className="error-hint">
            If the problem persists, the frontend may have severe bugs.
          </div>

        </div>
      </div>
    );
  }

  // --------------------------------------------------
  // CONTROL PANEL
  // --------------------------------------------------
  const layoutClass =
    layout === "top-center"
      ? "page page-top-center"
      : layout === "top-left"
      ? "page page-top-left"
      : layout === "top-right"
      ? "page page-top-right"
      : "page page-center-center";

  const themeClass = darkMode ? "dark" : "light";

  function ControlPanel({ layout, setLayout, darkMode, setDarkMode, shuffle, setShuffle, shuffleq, setShuffleq }) {
    return (
      <div className="panel-grid">

        {/* Header */}
        <button
          type="button"
          className="panel-title"
          onClick={() => setCollapsed(c => !c)}
        >
          ⚙ TORQUIZ Control Panel {collapsed ? "▼" : "▲"}
        </button>

        {/* Nur anzeigen wenn offen */}
        {!collapsed && (
          <>
            {/* Zeile 1 */}
            <select
              className="panel-control"
              value={layout}
              onChange={(e) => setLayout(e.target.value)}
            >
              <option value="center-center">Center</option>
              <option value="top-center">Top</option>
              <option value="top-left">Left</option>
              <option value="top-right">Right</option>
            </select>

            <label className="panel-toggle">
              <input
                type="checkbox"
                checked={shuffle}
                onChange={() => setShuffle(s => !s)}
              />
              🔀 Shuffle Answers
            </label>

            {/* Zeile 2 */}
            <label className="panel-toggle">
              <input
                type="checkbox"
                checked={darkMode}
                onChange={() => setDarkMode(d => !d)}
              />
              🌙 Dark Mode
            </label>

            <label className="panel-toggle">
              <input
                type="checkbox"
                checked={shuffleq}
                onChange={() => setShuffleq(s => !s)}
              />
              🔀 Shuffle Questions
            </label>

            {/* Zeile 3 - Buttons */}
            <button
              className="panel-button"
              onClick={() => {
                stop();
                //setCollapsed(true);
              }}
            >
              🔁 Stop
            </button>

            <button
              className="panel-button"
              onClick={() => {
                restart();
                // setCollapsed(true);
              }}
            >
              🔁 Restart
            </button>
          </>
        )}

      </div>
    );
  }

  // -----------------------------
  // LOADING PAGE
  // -----------------------------
  function ShowLoadingPage() {
    return (
      <div className="page page-center-center">
        <div className="card">
          <h2>⏳ Lade Quiz...</h2>
        </div>
      </div>
    );
  }

  // --------------------------------------------------
  // QUIZ SELECTION SCREEN
  // --------------------------------------------------
  function ShowQuizSelectionScreen() {
    return (
      <div className={`${layoutClass} ${themeClass}`}>

        <div className={`card ${uiState}`}>
          {/* Control Panel einbinden */}
          <ControlPanel
            layout={layout}
            setLayout={setLayout}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            shuffle={shuffle}
            setShuffle={setShuffle}
            shuffleq={shuffleq}
            setShuffleq={setShuffleq}
          />

          <h2 className="title-center">📚 Quiz auswählen</h2>

          {quizzes.length === 0 && <p>Lade Quizzes...</p>}

          {quizzes.map(q => (
            <button
              key={q.title}
              className="button-primary"
              onClick={() => loadQuestion(q)}
            >
              ▶ {q.name} {q.protected && " 🔒"}
            </button>
          ))}
        </div>

      </div>
    );
  }

  // -----------------------------
  // PASSWORD DIALOG
  // -----------------------------
  function ShowPasswordDialog() {
    return (
      <div className="page page-center-center">
        <div className="card">
          <h2 className="title-center">🔒 {pendingQuiz.name}</h2>
          <b className="title-center">Dieses Quiz ist passwortgeschützt</b>

          <div className="password-wrapper">
            <input
              className="input-primary"
              type={showPassword ? "text" : "password"}
              value={quizPassword}
              onChange={(e) => setQuizPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  unlockQuiz()
                }
              }}
              placeholder="Passwort eingeben"
            />
          </div>

          <label className="show-password-toggle">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={() => setShowPassword(!showPassword)}
            />
            Passwort anzeigen
          </label>

          {authError && (
            <p>{authError}</p>
          )}

          <div className="button-row">
            <button
              className="button-primary"
              onClick={() => {
                setShowPassword(false)
                unlockQuiz()
              }}
            >
              Quiz Öffnen
            </button>

            <button
              className="button-secondary"
              onClick={() => {
                setShowPassword(false)
                setShowPasswordModal(false);
                stop()
              }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    )
  }

  // -----------------------------
  // FINISHED PAGE
  // -----------------------------
  function ShowFinishedPage() {
    return (
      <div className={`${layoutClass} ${themeClass}`}>

        <div className={`card ${uiState}`}>
          {/* Control Panel einbinden */}
          <ControlPanel
            layout={layout}
            setLayout={setLayout}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            shuffle={shuffle}
            setShuffle={setShuffle}
            shuffleq={shuffleq}
            setShuffleq={setShuffleq}
          />

          <div style={{ textAlign: "center", fontSize: "1.25rem" }}>
            <b>{question.quiz_name}</b>
          </div>

          <h2>🏁 Fertig!</h2>
          <h3>Score: {question.quiz_score} / {question.quiz_total}</h3>

          <button className="button-primary" onClick={downloadResult}>
            💾 Ergebnis herunterladen
          </button>

          <button className="button-secondary" onClick={restart}>
            🔁 Neustart
          </button>
          <button className="button-secondary" onClick={stop}>
            🛑 Stop
          </button>
        </div>

      </div>
    );
  }

  // -----------------------------
  // CURRENT QUESTION
  // -----------------------------
  function ShowQuestionPage() {
    const progress = Math.round((question.quiz_current / question.quiz_total) * 100);

    return (
      <div className={`${layoutClass} ${themeClass}`}>

        {/* Card */}
        <div className={`card ${uiState}`}>
          {/* Control Panel einbinden */}
          <ControlPanel
            layout={layout}
            setLayout={setLayout}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            shuffle={shuffle}
            setShuffle={setShuffle}
            shuffleq={shuffleq}
            setShuffleq={setShuffleq}
          />

          {/* Header */}
          <div style={{ textAlign: "center", fontSize: "1.25rem" }}>
            <b>{question.quiz_name}</b>
          </div>

          <div className="header">
            <div>Score: {question.quiz_score}</div>
            <div>{question.quiz_current} / {question.quiz_total}</div>
          </div>

          {/* 📊 Progress Bar */}
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Question */}
          <h3>{question.question_title}</h3>

          {/* Options */}
          <div className="options">
            {Object.entries(question.options).map(([key, val]) => {

              const isOptionSelected = selectedOption.includes(key);
              const isOptionAnswerCorrect = answerResult && answerResult.correct_answers.includes(key);
              const isMultipleChoice = question.question_type === "multiple-choice";
              const isTrueFalse = question.question_type === "true-false";

              // Highlighting Standard für Multiple Choice: Blau = ausgewählt, Grün = richtig, Rot = falsch
              return (
                <button
                  key={key}
                  className="option"
                  onClick={() => toggleAnswer(key)}
                  disabled={!!answerResult}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    transition: "all 0.15s ease",

                    background:
                      answerResult
                      ? isOptionSelected          // Nach der Bewertung
                        ? isOptionAnswerCorrect   // * Antwort ist selektiert
                          ? "#d1f7d6"           //   * und richtig
                          : "#ffd6d6"           //   * aber falsch
                        : isOptionAnswerCorrect   // * Anwort ist nicht selektiert
                          ? "#d1f7d6"           //   * aber richtig
                          : "#f7f7f7"           //   * und auch falsch (neutral)
                      : isOptionSelected          // # Vor der Bewertung
                        ? "#dbeafe"             // * selektiert
                        : "#f7f7f7",            // * nicht selektiert (neutral)

                    border:
                      answerResult                      
                      ? isOptionSelected          // Nach der Bewertung
                        ? isOptionAnswerCorrect   // * Antwort ist selektiert
                          ? "2px solid #4caf50" //   * und richtig
                          : "2px solid #f44336" //   * aber falsch
                        : isOptionAnswerCorrect   // * Anwort ist nicht selektiert
                        ? "1px solid #f44336"   //   * aber richtig
                        : "1px solid #ddd"      //   * und auch falsch (neutral)
                      : isOptionSelected          // # Vor der Bewertung
                        ? "2px solid #3b82f6"   // * selektiert
                        : "1px solid #ddd",     // * nicht selektiert (neutral)
                    
                    cursor: answerResult ? "default" : "pointer"
                  }}
                >

                  {/* Selection Indicator */}
                  <span
                    className={
                      isMultipleChoice
                        ? `checkbox ${isOptionSelected ? "selected" : ""}`
                        : `radio ${isOptionSelected ? "selected" : ""}`
                    }
                  />

                  {/* Option Label */}
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span>
                      {isTrueFalse ? null : <strong>{key})</strong>}
                    </span>
                    <span style={{ marginLeft: isTrueFalse ? "0px" : "20px" }}>
                      {val}
                    </span>
                  </div>

                </button>
              );
            })}
          </div>

          {/* Prüfen Button */}
          {!answerResult && (
            <button
              className="button-primary"
              onClick={answer}
              disabled={selectedOption.length === 0}
              style={{
                opacity: selectedOption.length === 0 ? 0.5 : 1,
                cursor: selectedOption.length === 0 ? "not-allowed" : "pointer"
              }}
            >
              Antwort prüfen
            </button>
          )}

          {/* Feedback */}
          {answerResult && (
            <div className="feedback">
              {answerResult.correct
                ? "✅ Richtig!"
                : `❌ Falsch! (✅ ${answerResult ? answerResult.correct_answers.join(", ") : ""})`}
            </div>
          )}

          <div
            className="tooltip-wrapper"
            onMouseEnter={() => {
              timeoutRef.quiz_current = setTimeout(() => {
                setShowTooltip(true);
              }, 2000);
            }}
            onMouseLeave={() => {
              clearTimeout(timeoutRef.quiz_current);
              setShowTooltip(false);
            }}
            onMouseMove={(e) => {
              setTooltipPos({
                x: e.clientX,
                y: e.clientY
              });
            }}
          >
            {/* Weiter Button */}
            {answerResult && (
              <button className="button-primary" onClick={next}>
                Weiter →
              </button>
            )}

            {/* Tooltip */}
            {showTooltip && (
              <div
                className="tooltip"
                style={{
                  position: "fixed",
                  left: tooltipPos.x + 5,  // rechts von Maus
                  top: tooltipPos.y - 25,   // oberhalb
                  pointerEvents: "none"
                }}
              >
                Für nächste Frage klicken oder [Space] oder [Enter] drücken.
              </div>
            )}
          </div>
        </div> 
      </div>
    );
  }

  // --------------------------------------------------
  // Control
  // --------------------------------------------------
  try {
    if (!apiAlive)
      return ShowApiErrorPage()
    else if (!quizzes)
      return ShowLoadingPage()
    else if (!quiz) {
      if (showPasswordModal)
        return ShowPasswordDialog()
      else
        return ShowQuizSelectionScreen()
    }
    else if (question) {
      if (question.quiz_finished)
        return ShowFinishedPage()
      else 
        return ShowQuestionPage()
    }
    else
      throw new Error("Control state error");
  } catch (err) {
    console.error("Internal controller error:", err);
    return ShowControllerErrorPage(err?.message ?? 'unknown')
  }
}

export default App;
